import { useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { LOGO_URL } from "@/lib/quoteContent";
import { TERMS_SECTIONS } from "@/pages/Terms";

export default function AgreeTerms() {
  const { slug } = useParams();
  const [reachedEnd, setReachedEnd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  function onScroll(e) {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) setReachedEnd(true);
  }

  async function agree() {
    setLoading(true);
    try {
      await axios.post(`${API}/public/quotes/${slug}/agree-terms`);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong, please try again");
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div
        data-testid="agree-terms-done"
        className="min-h-screen bg-[#21201F] text-white flex flex-col items-center justify-center px-6 text-center"
      >
        <img src={LOGO_URL} alt="Wedding Video Essex" className="h-20 w-auto object-contain brightness-0 invert opacity-95 mb-10" />
        <span className="w-14 h-14 rounded-full bg-[#CCBA56] flex items-center justify-center mb-6">
          <Check className="w-7 h-7 text-[#1F1E1D]" strokeWidth={2.5} />
        </span>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-4">All done. Thank you!</h1>
        <p className="text-white/70 max-w-md leading-relaxed">
          Your agreement has been recorded. That's everything sorted, now we
          can't wait for the big day!
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="agree-terms-page">
      <div className="bg-[#21201F] text-white px-6 py-12 md:py-16 text-center">
        <img src={LOGO_URL} alt="Wedding Video Essex" className="h-16 md:h-20 w-auto object-contain brightness-0 invert opacity-95 mx-auto mb-6" />
        <div className="text-xs tracking-overline uppercase text-[#A9927D] mb-3 font-medium">
          One Small Piece Of Housekeeping
        </div>
        <h1 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">
          Our Terms &amp; Conditions
        </h1>
        <p className="text-white/70 max-w-lg mx-auto mt-4 text-sm leading-relaxed">
          Please read through to the end, then tap agree. It only takes a
          minute and your booking stays exactly as it is.
        </p>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div
          onScroll={onScroll}
          data-testid="agree-terms-scroll-box"
          className="h-[55vh] overflow-y-auto border border-[#D1CEC2] bg-white p-6 space-y-6"
        >
          {TERMS_SECTIONS.map(([title, body]) => (
            <div key={title}>
              <div className="text-[#1F1E1D] font-medium text-sm mb-1">{title}</div>
              {body.split("\n\n").map((p, i) => (
                <p key={i} className="text-[#5C5A56] text-[13px] leading-relaxed mb-2">{p}</p>
              ))}
            </div>
          ))}
          <p className="text-[#8A8782] text-[13px] pt-2 border-t border-[#E5E3DB]">
            These Terms &amp; Conditions form the basis of the agreement between
            the Customer and Wedding Video Essex LTD.
          </p>
        </div>
        {!reachedEnd && (
          <p className="text-[11px] text-[#8A8782] mt-3" data-testid="agree-scroll-hint">
            Scroll to the end of the terms to continue.
          </p>
        )}
        <button
          onClick={agree}
          disabled={!reachedEnd || loading}
          data-testid="agree-terms-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#2F2E2E] hover:bg-[#454341] text-white px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors disabled:opacity-50"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
          I Agree To The Terms &amp; Conditions
        </button>
      </div>
    </div>
  );
}
